import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { AutoAwesome, TrendingUp, Lock, Bolt, Verified } from '@mui/icons-material';
import { authBackground, authCardSx, authGlassCardSx } from './authStyles';
import logo from '../../images/CRM-Logo-large.png';

// ─────────────────────────────────────────────────────────────────────────────
// AuthShell — shared layout for all logged-out / account-entry screens.
//
//   • split mode (default): two panels. Left = a light, airy brand hero (white
//     canvas + subtle teal washes + frosted-white floating cards). Right = the
//     form column (passed as children). The hero hides below md.
//   • centered mode (`centered`): a single frosted-white card centered on the
//     white+gradient background — for the set-password / portal screens.
// ─────────────────────────────────────────────────────────────────────────────

// Frosted-white floating card base (light-on-light, lifted by soft shadow).
const glassCard = {
    position: 'absolute',
    borderRadius: '22px',
    background: 'rgba(255,255,255,0.75)',
    border: '1px solid rgba(255,255,255,0.9)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    boxShadow: '0 24px 60px -28px rgba(30,58,82,0.28), 0 2px 6px rgba(16,42,66,0.05)',
};

// Soft teal-tinted square icon tile used inside the glass cards.
const iconTile = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '10px',
    background: 'linear-gradient(135deg, rgba(66,120,152,0.16), rgba(66,120,152,0.06))',
    border: '1px solid rgba(66,120,152,0.14)',
    color: '#427898',
    flexShrink: 0,
};

function AuthHero() {
    return (
        <Box
            sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                justifyContent: 'space-between',
                width: '56%',
                position: 'relative',
                overflow: 'hidden',
                px: 8,
                py: 7,
                background: 'transparent',
                '@keyframes floatA': {
                    '0%, 100%': { transform: 'translateY(0) rotate(-8deg)' },
                    '50%': { transform: 'translateY(-16px) rotate(-8deg)' },
                },
                '@keyframes floatB': {
                    '0%, 100%': { transform: 'translateY(0) rotate(7deg)' },
                    '50%': { transform: 'translateY(-11px) rotate(7deg)' },
                },
                '@keyframes floatC': {
                    '0%, 100%': { transform: 'translate(-50%, 0) rotate(-2deg)' },
                    '50%': { transform: 'translate(-50%, -9px) rotate(-2deg)' },
                },
                '@keyframes floatChip': {
                    '0%, 100%': { transform: 'translateY(0) rotate(3deg)' },
                    '50%': { transform: 'translateY(-7px) rotate(3deg)' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '& [data-float]': { animation: 'none !important' },
                },
            }}
        >
            {/* Very soft teal glow behind the hero */}
            <Box sx={{
                position: 'absolute',
                top: '38%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 640,
                height: 640,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(66,120,152,0.10) 0%, rgba(66,120,152,0.04) 40%, transparent 68%)',
                filter: 'blur(6px)',
                pointerEvents: 'none',
            }} />

            {/* Zone 1 · Logo (natural color) */}
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <img src={logo} alt="Plankton CRM" style={{ width: 178 }} />
            </Box>

            {/* Zone 2 · Floating glass cards */}
            <Box sx={{
                position: 'relative',
                zIndex: 2,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Box sx={{ position: 'relative', width: 460, height: 380 }}>

                    {/* Back card — faux area chart */}
                    <Box
                        data-float
                        sx={{
                            ...glassCard,
                            left: 6,
                            top: 34,
                            width: 246,
                            p: 2.5,
                            opacity: 0.7,
                            filter: 'blur(1px)',
                            animation: 'floatA 8s ease-in-out infinite',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{ ...iconTile, width: 26, height: 26 }}>
                                <TrendingUp sx={{ fontSize: 15 }} />
                            </Box>
                            <Box sx={{ height: 8, width: 96, borderRadius: 4, background: 'rgba(66,120,152,0.18)' }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: 58 }}>
                            {[40, 62, 48, 78, 66, 92, 74].map((h, i) => (
                                <Box key={i} sx={{
                                    flex: 1,
                                    height: `${h}%`,
                                    borderRadius: '4px 4px 2px 2px',
                                    background: 'linear-gradient(180deg, rgba(66,120,152,0.5), rgba(66,120,152,0.12))',
                                }} />
                            ))}
                        </Box>
                    </Box>

                    {/* Back card — contacts list */}
                    <Box
                        data-float
                        sx={{
                            ...glassCard,
                            right: 2,
                            top: 62,
                            width: 232,
                            p: 2.5,
                            opacity: 0.78,
                            animation: 'floatB 9s ease-in-out infinite 0.4s',
                        }}
                    >
                        {[0.95, 0.8, 0.62].map((o, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: i < 2 ? 1.75 : 0, opacity: o }}>
                                <Box sx={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(78,144,181,0.55), rgba(66,120,152,0.3))', flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ height: 7, width: `${80 - i * 14}%`, borderRadius: 4, background: 'rgba(66,120,152,0.28)', mb: 0.75 }} />
                                    <Box sx={{ height: 6, width: `${55 - i * 8}%`, borderRadius: 4, background: 'rgba(66,120,152,0.14)' }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Front card — AI insight + KPI */}
                    <Box
                        data-float
                        sx={{
                            ...glassCard,
                            left: '50%',
                            top: 118,
                            width: 316,
                            p: 3,
                            opacity: 1,
                            background: 'rgba(255,255,255,0.92)',
                            border: '1px solid rgba(255,255,255,1)',
                            boxShadow: '0 40px 80px -30px rgba(30,58,82,0.32), 0 2px 8px rgba(16,42,66,0.06)',
                            animation: 'floatC 7s ease-in-out infinite',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
                            <Box sx={iconTile}>
                                <AutoAwesome sx={{ fontSize: 17 }} />
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.3, color: '#1E293B' }}>
                                AI Insight
                            </Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.6)' }} />
                                <Typography sx={{ fontSize: 11, color: '#94A3B8', letterSpacing: 0.4 }}>Live</Typography>
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mb: 0.5 }}>
                            Revenue this month
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 2.5 }}>
                            <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1, color: '#1a2332' }}>
                                CHF 128,400
                            </Typography>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.25,
                                px: 0.9, py: 0.3, borderRadius: 999,
                                background: 'rgba(34,197,94,0.12)',
                                border: '1px solid rgba(34,197,94,0.3)',
                            }}>
                                <TrendingUp sx={{ fontSize: 13, color: '#16A34A' }} />
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#16A34A' }}>12.4%</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 46 }}>
                            {[34, 52, 44, 68, 58, 84, 100].map((h, i) => (
                                <Box key={i} sx={{
                                    flex: 1,
                                    height: `${h}%`,
                                    borderRadius: '5px 5px 3px 3px',
                                    background: i === 6
                                        ? 'linear-gradient(180deg, #4e90b5, #427898)'
                                        : 'linear-gradient(180deg, rgba(66,120,152,0.45), rgba(66,120,152,0.12))',
                                }} />
                            ))}
                        </Box>
                    </Box>

                    {/* Floating notification chip */}
                    <Box
                        data-float
                        sx={{
                            ...glassCard,
                            right: 14,
                            top: 46,
                            width: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.75,
                            py: 1.1,
                            background: 'rgba(255,255,255,0.95)',
                            animation: 'floatChip 6.5s ease-in-out infinite 0.2s',
                        }}
                    >
                        <Box sx={{ ...iconTile, width: 26, height: 26 }}>
                            <Bolt sx={{ fontSize: 15 }} />
                        </Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}>
                            3 deals ready to close
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Zone 3 · Value proposition */}
            <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 460 }}>
                <Typography sx={{
                    fontSize: 34,
                    fontWeight: 600,
                    letterSpacing: -0.8,
                    lineHeight: 1.15,
                    color: '#1a2332',
                    mb: 1.5,
                }}>
                    Clarity for every part
                    <br />of your business.
                </Typography>
                <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: '#64748B', mb: 3.5, maxWidth: 400 }}>
                    Customers, projects and invoices — unified, and quietly made smarter by AI.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                    {[
                        { icon: <Lock sx={{ fontSize: 14 }} />, label: 'Secure' },
                        { icon: <AutoAwesome sx={{ fontSize: 14 }} />, label: 'Intelligent' },
                        { icon: <Verified sx={{ fontSize: 14 }} />, label: 'Swiss-made' },
                    ].map((f) => (
                        <Box key={f.label} sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            px: 1.5, py: 0.75, borderRadius: 999,
                            background: 'rgba(66,120,152,0.08)',
                            border: '1px solid rgba(66,120,152,0.16)',
                            color: '#427898',
                        }}>
                            {f.icon}
                            <Typography sx={{ fontSize: 12.5, fontWeight: 500, letterSpacing: 0.2 }}>{f.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default function AuthShell({ children, centered = false, cardMaxWidth = 400, formMaxWidth = 400 }) {
    // Centered single-card mode (set-password / portal screens)
    if (centered) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: authBackground,
                px: 2,
            }}>
                <Paper elevation={0} sx={{ ...authCardSx, maxWidth: cardMaxWidth }}>
                    {children}
                </Paper>
            </Box>
        );
    }

    // Split mode (login / register)
    return (
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', background: authBackground }}>
            <AuthHero />

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    px: { xs: 3, sm: 6, md: 8 },
                    overflowY: 'auto',
                    animation: 'fadeSlideIn 0.4s ease both',
                    '@keyframes fadeSlideIn': {
                        from: { opacity: 0, transform: 'translateY(16px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                    },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: formMaxWidth, py: 4 }}>
                    {/* Mobile-only logo (sits above the box) */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
                        <img src={logo} alt="Plankton CRM" style={{ width: 160 }} />
                    </Box>
                    {/* Translucent login box floating over the bloom */}
                    <Box sx={authGlassCardSx}>
                        {children}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
